import {
  DialogSub,
  DialogSubContent,
  DialogSubTrigger,
} from '@src/design-system/dialog';
import { CustomErrorMessage } from '@src/design-system/error-message';
import { cn } from '@src/design-system/helpers';
import { inputVariants } from '@src/design-system/input';
import { Label } from '@src/design-system/label';
import { PhoneNumberInput } from '@src/design-system/phone-number-input';
import { Textfield } from '@shared/fields/text-field';
import { ErrorMessage, Form } from 'formik';
import { ChevronDown, ChevronRightIcon } from 'lucide-react';
import type { FC } from 'react';

import { AssigneeInfo } from '../components/assignee-info';
import { Edit } from 'iconsax-react';
import ManageReferalDialog from '@features/leads/pages/detail/modals/manage-referal-dialog';
import { HasPermission } from '@features/roles/guards/has-permission';
import { entities } from '@shared/constants/entities';
import { useHasPermission } from '@shared/hooks/use-has-permission';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '@src/design-system/popover';

import { ReferalsList } from '@shared/components/referals-list';
import { ProgramsList } from '@src/shared/components/programs-list';

import { type TSingleLeadFormValues } from '../helper';
import AssignToSubDialog from '../dialogs/assign-to-sub-dialog';
import { useUserTypeCheck } from '@src/shared/hooks/use-user-type-check';

const subModals = [
  {
    label: 'Select an Assignee',
    subModalContent: <AssignToSubDialog />,
  },
];

export const CreateLeadForm: FC<{
  values: TSingleLeadFormValues;
  setFieldValue: (field: string, value: any) => void;
}> = ({ values, setFieldValue }) => {
  const hasPermission = useHasPermission();
  const canViewReferals = hasPermission(entities.leadReferal, 'view');
  const isPartner = useUserTypeCheck("PARTNER");

  return (
    <Form>
      <div className='mb-4'>
        <Label tabIndex={1}>Interested in</Label>
        <Popover>
          <PopoverTrigger
            className={cn(
              inputVariants(),
              'w-full cursor-pointer flex py-3 justify-between items-center font-normal disabled:opacity-70 text-gray-500 text-sm'
            )}
          >
            {values.programs?.length ? (
              <span className='text-gray-1000 flex flex-wrap gap-2'>
                {values.programs.slice(0, 3)?.map((p) => (
                  <span
                    key={p.id}
                    className='bg-primary-100 rounded-md py-1.5 max-w-[120px] px-2 truncate text-xs'
                    title={p.name}
                  >
                    {p.name}
                  </span>
                ))}
                {values.programs?.length > 3 && (
                  <span className='bg-primary-100 rounded-md py-1.5 max-w-[120px] px-2 truncate text-xs'>
                    +{values.programs?.length - 3}
                  </span>
                )}
              </span>
            ) : (
              'Select Programs'
            )}

            <ChevronDown
              size={16}
              className='shrink-0 self-center'
            />
          </PopoverTrigger>
          <PopoverContent
            withPortal={false}
            style={{
              width: 'var(--radix-popover-trigger-width)',
            }}
            className='p-0 z-50'
          >
            <ProgramsList
              props={{ infoItemProps: { withCheckbox: true } }}
              onClickItem={(program, isSelected) => {
                const programs = values.programs ?? [];
                const value = isSelected
                  ? programs?.filter((p) => p.id !== program.id)
                  : [...programs, program];
                setFieldValue('programs', value);
              }}
              isItemSelected={(program) => {
                return !!values.programs?.some((p) => p.id === program.id);
              }}
            />
          </PopoverContent>
        </Popover>

        <ErrorMessage
          name='programs'
          component={CustomErrorMessage}
        />
      </div>

      <Textfield
        name='name'
        type='text'
        placeholder='Name'
        label='Name'
      />

      <div className='grid grid-cols-2 gap-2 mt-4'>
        <Textfield
          name='email'
          type='email'
          placeholder='Email'
          label='Email'
        />
        <div className='flex flex-col gap-1 mt-2'>
          <Label className='mb-1'>Phone Number</Label>
          <PhoneNumberInput
            name='phone_number'
            placeholder='ex: +20124356762'
            onChange={(value) => setFieldValue('phone_number', value)}
            value={values.phone_number}
          />
          <ErrorMessage
            name='phone_number'
            component={CustomErrorMessage}
          />
        </div>
      </div>
      <div className='my-5'>
        <div className='flex items-center justify-between w-full'>
          <Label>Refer (optional)</Label>
          <HasPermission
            entity={entities.leadReferal}
            action='view'
          >
            <DialogSub>
              <DialogSubTrigger
                value='source'
                className='mr-2 wrap-break-word cursor-pointer underline flex items-center gap-1 text-xs text-purple-900 w-auto'
              >
                <Edit size={16} /> Manage Source
              </DialogSubTrigger>
              <DialogSubContent value='source'>
                <ManageReferalDialog withFullHeight={false} />
              </DialogSubContent>
            </DialogSub>
          </HasPermission>
        </div>

        <div>
          <Popover>
            <PopoverTrigger
              disabled={!canViewReferals}
              className={cn(
                inputVariants(),
                'w-full cursor-pointer flex justify-between items-center font-normal disabled:opacity-70 text-gray-500 text-sm'
              )}
            >
              {values.referal?.name ? (
                <span className='text-gray-1000'>{values.referal?.name}</span>
              ) : (
                'Select Referal'
              )}

              <ChevronDown size={16} />
            </PopoverTrigger>
            <PopoverContent
              withPortal={false}
              style={{
                width: 'var(--radix-popover-trigger-width)',
              }}
              className='p-4'
            >
              <ReferalsList
                props={{
                  infiniteListProps: {
                    props: {
                      searchContainerProps: { className: 'p-0' },
                      listContainerProps: { className: 'p-0' },
                      listProps: { className: 'gap-0' },
                    },
                  },
                }}
                isItemSelected={(referal) =>
                  String(values.referal?.id) === String(referal.id)
                }
                onClickItem={(referal) => setFieldValue('referal', referal)}
                CloseListTrigger={PopoverClose}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {!isPartner && <div>
        <Label>Assign to (optional)</Label>
        <DialogSub>
          {subModals.map((subModal) => (
            <div
              key={subModal.label}
              className='first:mb-2'
            >
              <DialogSubTrigger
                value={subModal.label}
                className={cn(
                  inputVariants({ variant: 'fill', size: 'lg' }),
                  'flex text-sm min-w-full justify-between items-center cursor-pointer'
                )}
              >
                <span className='flex items-center gap-2'>{subModal.label}</span>
                <ChevronRightIcon size={16} />
              </DialogSubTrigger>

              <DialogSubContent value={subModal.label}>
                {subModal.subModalContent}
              </DialogSubContent>
            </div>
          ))}
        </DialogSub>

        {values.assignee && (
          <>
            <AssigneeInfo
              className='border border-pale-border rounded-xl p-2 mt-4'
              {...values.assignee}
            />
            <ErrorMessage
              name='assignee'
              component={CustomErrorMessage}
            />
          </>
        )}
      </div>}
    </Form>
  );
};
